import { toQueryString } from '../../framework/apis/apiGenerator';
import { TypeUtils } from '../../framework/utils/UtilityTypes';
import { pageKeywords, pagePaths } from '../constants/Page';
import { EngeneNavigateFunction } from '../hooks/navigate';
import {
  DeadlineMode,
  DeadlineSearchResultResults,
  HalfPeriod,
  Notice,
} from '../models/gen/models';

type CommonDeadline = Pick<
  DeadlineSearchResultResults,
  'category' | 'mode' | 'message' | 'ToDoWarnings'
>;

type RequiredFromDeadlineResult<T extends keyof DeadlineSearchResultResults> =
  Required<Pick<DeadlineSearchResultResults, T>>;

type DailyDeadline = CommonDeadline & RequiredFromDeadlineResult<'date'>;
type MonthlyDeadline = CommonDeadline &
  RequiredFromDeadlineResult<'year' | 'month'> &
  Pick<DeadlineSearchResultResults, 'half'>;

export type AssignDeadline = DailyDeadline &
  RequiredFromDeadlineResult<'matterName' | 'assignMessageId'>;
export type AttendanceDeadline = MonthlyDeadline &
  RequiredFromDeadlineResult<'half'>;
export type ExpenseDeadline = MonthlyDeadline &
  RequiredFromDeadlineResult<'half'>;
export type InvoiceDeadline = MonthlyDeadline;
export type InvoiceInputDeadline = MonthlyDeadline;
export type PayslipDeadline = MonthlyDeadline;
export type ShiftApplyDeadline = MonthlyDeadline;
export type WorkDocReportDeadline = DailyDeadline &
  RequiredFromDeadlineResult<'matterName' | 'dailyMatterId'>;
export type WorkReportDeadline = DailyDeadline &
  RequiredFromDeadlineResult<'matterIndex' | 'matterName' | 'userCode'>;

export type MessageClickOptions = {
  matterIndex?: number;
  onOpen?: Consumer<number>;
  outsourcerCode?: string;
  userCode?: string;
};

// ----------------------------------------------------------------------------
// properties
// ----------------------------------------------------------------------------

/**
 * 警告・やること (日次業務)
 */
const dailyDeadlines: ReadonlyArray<DeadlineMode> = [
  DeadlineMode.Assign,
  DeadlineMode.Daily,
  DeadlineMode.Report,
  DeadlineMode.Work,
] as const;

/**
 * 警告・やること (月次業務)
 */
const monthlyDeadlines: ReadonlyArray<DeadlineMode> = [
  DeadlineMode.Attendance,
  DeadlineMode.Expense,
  DeadlineMode.Invoice,
  DeadlineMode.InvoiceInput,
  DeadlineMode.Monthly,
  DeadlineMode.Payslip,
  DeadlineMode.Shift,
] as const;

// ----------------------------------------------------------------------------
// methods
// ----------------------------------------------------------------------------

/**
 * 遷移先URLを生成する
 *
 * @param element -
 * @param options -
 * @returns
 */
const generateUrl = (
  element: DeadlineSearchResultResults | Notice,
  options?: MessageClickOptions
) => {
  if (DeadlineUtils.isDeadline(element)) {
    if (DeadlineUtils.isAssign(element)) {
      return `${pagePaths.ASSIGNMENT_SE}/${element.assignMessageId}`;
    }
    if (DeadlineUtils.isAttendance(element)) {
      return `${pagePaths.ATTENDANCE_REPORT}${toQueryString({
        year: element.year,
        month: element.month,
        half:
          element.half === HalfPeriod.FirstHalf
            ? 1
            : element.half === HalfPeriod.SecondHalf
            ? 2
            : '',
      })}`;
    }
    if (DeadlineUtils.isExpense(element)) {
      return `${pagePaths.EXPENSE_REIMBURSEMENT}${toQueryString({
        year: element.year,
        month: element.month,
        half:
          element.half === HalfPeriod.FirstHalf
            ? 1
            : element.half === HalfPeriod.SecondHalf
            ? 2
            : '',
      })}`;
    }
    if (DeadlineUtils.isInvoice(element)) {
      return `${pagePaths.INVOICE}${toQueryString({
        year: element.year,
        month: element.month,
      })}`;
    }
    if (DeadlineUtils.isPayslip(element)) {
      return `${pagePaths.PAYSLIP}${toQueryString({
        year: element.year,
        month: element.month,
      })}`;
    }
    if (DeadlineUtils.isWorkDocReport(element)) {
      return `${pagePaths.WORK_REPORT}/${element.dailyMatterId}/${pageKeywords.mode.EDIT}`;
    }
    if (DeadlineUtils.isShiftApply(element)) {
      return `${pagePaths.SHIFT_APPLICATION}${toQueryString({
        year: element.year,
        month: element.month,
      })}`;
    }
    if (DeadlineUtils.isWorkReport(element)) {
      return `${pagePaths.WORK_CONTENT}/${element.date}/${options?.userCode}/${
        pageKeywords.mode.EDIT
      }${toQueryString({
        outsourcerCode: options?.outsourcerCode,
        index: options?.matterIndex,
      })}`;
    }
    return undefined;
  } else if (TypeUtils.isNonNil(options?.onOpen)) {
    return undefined;
  }
};

/**
 * 警告・やることの表示名を取得する
 *
 * @param element -
 * @returns
 */
const getDeadlineName = (element: DeadlineSearchResultResults) => {
  switch (element.mode) {
    case DeadlineMode.Assign:
      return '手配(受注)';
    case DeadlineMode.Attendance:
      return '勤怠申請';
    case DeadlineMode.Expense:
      return '経費申請';
    case DeadlineMode.Invoice:
      return '請求';
    case DeadlineMode.InvoiceInput:
      return '請求入力';
    case DeadlineMode.Payslip:
      return '給与確認';
    case DeadlineMode.Report:
      return '作業報告書提出';
    case DeadlineMode.Shift:
      return 'シフト申請';
    case DeadlineMode.Work:
      return '就業報告';
    default:
      return undefined;
  }
};

/**
 * 警告・やることをクリックできるかどうか判定する
 *
 * @param element -
 * @returns
 */
const isAbleToClick = (element: DeadlineSearchResultResults | Notice) =>
  !isDeadline(element)
    ? true // お知らせ
    : [
        DeadlineMode.Assign,
        DeadlineMode.Report,
        DeadlineMode.Work,
        DeadlineMode.Attendance,
        DeadlineMode.Expense,
        DeadlineMode.Invoice,
        DeadlineMode.Payslip,
        DeadlineMode.Shift,
      ].includes(element.mode);

/**
 * 警告・やること(手配・受注)であるか判定する
 *
 * @param element -
 * @returns
 */
const isAssign = (
  element: DeadlineSearchResultResults | Notice
): element is AssignDeadline =>
  isDaily(element) &&
  element.mode === DeadlineMode.Assign &&
  'matterName' in element &&
  'assignMessageId' in element;

/**
 * 警告・やること(勤怠申請)であるか判定する
 *
 * @param element
 * @returns
 */
const isAttendance = (
  element: DeadlineSearchResultResults | Notice
): element is AttendanceDeadline =>
  isMonthly(element) &&
  element.mode === DeadlineMode.Attendance &&
  'half' in element;

/**
 * 警告・やること(日次業務)であるか判定する
 *
 * @param element -
 * @returns
 */
const isDaily = (
  element: DeadlineSearchResultResults | Notice
): element is DailyDeadline =>
  isDeadline(element) &&
  dailyDeadlines.includes(element.mode) &&
  'date' in element;

/**
 * 警告・やることであるか判定する
 *
 * @param element -
 * @returns
 */
const isDeadline = (
  element: DeadlineSearchResultResults | Notice
): element is DeadlineSearchResultResults =>
  ('category' in element && 'mode' in element) || 'ToDoWarnings' in element;

/**
 * 警告・やること(経費申請)であるか判定する
 * @param element -
 * @returns
 */
const isExpense = (
  element: DeadlineSearchResultResults | Notice
): element is ExpenseDeadline =>
  isMonthly(element) &&
  element.mode === DeadlineMode.Expense &&
  'half' in element;

/**
 * 警告・やること(請求)であるか判定する
 *
 * @param element -
 * @returns
 */
const isInvoice = (
  element: DeadlineSearchResultResults | Notice
): element is InvoiceDeadline =>
  isMonthly(element) && element.mode === DeadlineMode.Invoice;

/**
 * 警告・やること(請求入力)であるか判定する
 *
 * @param element -
 * @returns
 */
const isInvoiceInput = (
  element: DeadlineSearchResultResults | Notice
): element is InvoiceInputDeadline =>
  isMonthly(element) && element.mode === DeadlineMode.InvoiceInput;

/**
 * 警告・やること(月次業務)であるか判定する
 *
 * @param element -
 * @returns
 */
const isMonthly = (
  element: DeadlineSearchResultResults | Notice
): element is MonthlyDeadline =>
  isDeadline(element) &&
  monthlyDeadlines.includes(element.mode) &&
  'year' in element &&
  'month' in element;

/**
 * 警告・やること(給与確認)であるか判定する
 *
 * @param element -
 * @returns
 */
const isPayslip = (
  element: DeadlineSearchResultResults | Notice
): element is PayslipDeadline =>
  isMonthly(element) && element.mode === DeadlineMode.Payslip;

/**
 * 警告・やること(シフト申請)であるか判定する
 *
 * @param element -
 * @returns
 */
const isShiftApply = (
  element: DeadlineSearchResultResults | Notice
): element is ShiftApplyDeadline =>
  isMonthly(element) && element.mode === DeadlineMode.Shift;

/**
 * 警告・やること(作業報告書提出)であるか判定する
 * @param element -
 * @returns
 */
const isWorkDocReport = (
  element: DeadlineSearchResultResults | Notice
): element is WorkDocReportDeadline =>
  isDaily(element) &&
  element.mode === DeadlineMode.Report &&
  'matterName' in element &&
  'dailyMatterId' in element;

/**
 * 警告・やること(就業報告)であるか判定する
 *
 * @param element -
 * @returns
 */
const isWorkReport = (
  element: DeadlineSearchResultResults | Notice
): element is WorkReportDeadline =>
  isDaily(element) &&
  element.mode === DeadlineMode.Work &&
  'matterIndex' in element &&
  'matterName' in element &&
  'userCode' in element;

/**
 * メッセージクリックイベント
 *
 * @param element -
 * @param navigate -
 * @param options -
 * @returns
 */
const onClickMessage = (
  element: DeadlineSearchResultResults | Notice,
  navigate: EngeneNavigateFunction,
  options?: MessageClickOptions
) => {
  if (DeadlineUtils.isDeadline(element)) {
    const url = generateUrl(element, options);
    if (TypeUtils.isNilString(url)) {
      return () => null;
    }
    return () => navigate(url);
  } else if (TypeUtils.isNonNil(options?.onOpen)) {
    return TypeUtils.isNonNilNumber(element.noticeId)
      ? () => options!.onOpen!(element.noticeId!)
      : undefined;
  }
};

const DeadlineUtils = {
  daily: dailyDeadlines,
  monthly: monthlyDeadlines,
  generateUrl,
  getName: getDeadlineName,
  isAbleToClick,
  isAssign,
  isAttendance,
  isDaily,
  isDeadline,
  isExpense,
  isInvoice,
  isInvoiceInput,
  isMonthly,
  isPayslip,
  isShiftApply,
  isWorkDocReport,
  isWorkReport,
  onClick: onClickMessage,
};

export default DeadlineUtils;
